import { useSetState } from '@domain-group/react-hooks'
import { Button, FormField, SidebarModal } from '@domain-group/suit-theme'

import useCampaign from '@hooks/useCampaign'
import useCampaignQuotes from '@hooks/useCampaignQuotes'

import CampaignSearch from '@components/CampaignSearch'

import type { CampaignModel } from '@models/types'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type { CampaignQuotePayload } from '@hooks/useCampaignQuote'
import type { CurrentUserPayload } from '@hooks/useCurrentUser'

import Campaign from './components/Campaign'

type DefaultState = {
  searchString: string,
  selectedCampaignId: undefined | number,
  selectedCampaignQuoteId: undefined | number,
}

const defaultState: DefaultState = {
  searchString: '',
  selectedCampaignId: undefined,
  selectedCampaignQuoteId: undefined,
}

interface CopyFromCampaignProps {
  callbacks: {
    afterAction: VoidFunction,
    closeModal: () => void,
    createQuoteFromExisting: (params: HandleCallbackActionParams) => void,
  },
  campaignQuotePayload: CampaignQuotePayload,
  currentUserPayload: CurrentUserPayload,
  requestOptions?: object,
}

const CopyFromCampaign = (props: CopyFromCampaignProps) => {
  const {
    callbacks: {
      afterAction,
      createQuoteFromExisting,
    },
    campaignQuotePayload: {
      callbacks: {
        createCampaignQuoteFromExisting: createFn,
      },
      campaignQuote,
    },
    currentUserPayload: {
      isAgencyAdmin,
    },
    requestOptions,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { searchString, selectedCampaignId, selectedCampaignQuoteId } = state

  const campaignPayload = useCampaign({ id: selectedCampaignId }, {
    performHttpRequests: !!selectedCampaignId,
    requestOptions: {
      include_campaign_image: true,
    },
  })

  const { campaign } = campaignPayload

  const campaignQuotesPayload = useCampaignQuotes({
    filters: {
      campaign_id: selectedCampaignId,
    },
    performHttpRequests: !!selectedCampaignId,
    requestOptions,
  })

  const { filteredCampaignQuotes } = campaignQuotesPayload

  return (
    <>
      <SidebarModal.Content hasFooter>
        {!selectedCampaignId && (
          <FormField label="Search Campaigns">
            <CampaignSearch
              callbacks={{
                onChange: value => setState({ searchString: value }),
                selectItem: (item: CampaignModel) => setState({
                  searchString: '',
                  selectedCampaignId: item.id,
                  selectedCampaignQuoteId: undefined,
                }),
              }}
              value={searchString}
            />
          </FormField>
        )}

        {!!selectedCampaignId && (
          <>
            <FormField label="Selected Campaign">
              <Campaign
                callbacks={{
                  deselectCampaign: () => setState({ selectedCampaignId: undefined }),
                }}
                campaign={campaign}
              />

            </FormField>

            <FormField label="* Select Quote" marginTop="large">
              <select onChange={e => setState({ selectedCampaignQuoteId: Number(e.target.value) })}>
                <option value="">Please Select...</option>
                {filteredCampaignQuotes.map(campaignQuote => (
                  <option key={campaignQuote.id} value={campaignQuote.id}>
                    {campaignQuote.title}
                  </option>
                ))}
              </select>
            </FormField>
          </>
        )}

      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          disabled={!selectedCampaignQuoteId}
          buttonStyle="primaryCreate"
          onClick={() => createQuoteFromExisting({
            callbacks: {
              // @ts-expect-error TS2322
              action: createFn,
              afterAction,
              // @ts-expect-error TS2322
              buildActionParams: () => [
                campaignQuote,
                selectedCampaignQuoteId,
                requestOptions,
              ],
            },
            toastText: 'Campaign Quote Created',
          })}
          size="large"
        >
          Copy Quote
        </Button>
      </SidebarModal.Footer>
    </>
  )
}

export default CopyFromCampaign
